import { useEffect, useRef, useCallback, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  CaretLeft,
  ChatCircle,
  Trash,
  PencilSimpleLine,
  Check,
  X,
} from "@phosphor-icons/react";
import { useAgentChatStore } from "../../stores/agentChat";
import { useDocumentStore } from "../../stores/document";
import { useEditorStore } from "../../stores/editor";
import { ChatMessageComponent } from "./ChatMessage";
import { ChatInput } from "./ChatInput";
import { ThinkingIndicator } from "./ThinkingIndicator";
import { cn } from "../../lib/cn";
import { APP_NAME, LeverMark } from "../../lib/brand";
import { springSnappy } from "../../lib/motion-config";

const spring = springSnappy;

function formatRelativeTime(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function ConversationListView() {
  const conversationList = useAgentChatStore((s) => s.conversationList);
  const currentId = useAgentChatStore((s) => s.currentConversationId);
  const switchConversation = useAgentChatStore((s) => s.switchConversation);
  const deleteConversation = useAgentChatStore((s) => s.deleteConversation);
  const renameConversation = useAgentChatStore((s) => s.renameConversation);
  const startNewConversation = useAgentChatStore((s) => s.startNewConversation);
  const toggleList = useAgentChatStore((s) => s.toggleConversationList);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingId) inputRef.current?.focus();
  }, [editingId]);

  const handleRename = (id: string) => {
    const trimmed = editDraft.trim();
    if (trimmed) renameConversation(id, trimmed);
    setEditingId(null);
  };

  const sorted = [...conversationList].sort(
    (a, b) => new Date(b.conversation.updatedAt).getTime() - new Date(a.conversation.updatedAt).getTime(),
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border-hairline)]">
        <div className="flex items-center gap-2">
          <motion.button
            onClick={toggleList}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="flex items-center justify-center h-5 w-5 rounded-[var(--radius-sm)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)] transition-colors duration-100"
          >
            <CaretLeft size={12} weight="bold" />
          </motion.button>
          <span className="text-[11px] font-semibold text-[var(--text-primary)]">Conversations</span>
        </div>
        <motion.button
          onClick={startNewConversation}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="flex items-center justify-center h-5 w-5 rounded-[var(--radius-sm)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)] transition-colors duration-100"
          title="New conversation"
        >
          <Plus size={11} weight="bold" />
        </motion.button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto py-1">
        {sorted.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full px-4 text-center">
            <ChatCircle size={24} className="text-[var(--text-muted)] mb-2" />
            <p className="text-[10px] text-[var(--text-muted)]">No conversations yet</p>
          </div>
        )}
        {sorted.map((item) => {
          const isActive = item.conversation.id === currentId;
          const isEditing = editingId === item.conversation.id;

          return (
            <motion.div
              key={item.conversation.id}
              layout
              className={cn(
                "group flex items-center gap-2 px-3 py-2 mx-1 rounded-[var(--radius-md)] cursor-pointer",
                "transition-colors duration-75",
                isActive
                  ? "bg-[var(--bg-surface-hover)] text-[var(--text-primary)]"
                  : "text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]",
              )}
              onClick={() => !isEditing && switchConversation(item.conversation.id)}
            >
              <ChatCircle size={12} className="shrink-0 text-[var(--text-muted)]" />
              {isEditing ? (
                <div className="flex items-center gap-1 flex-1 min-w-0">
                  <input
                    ref={inputRef}
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRename(item.conversation.id);
                      if (e.key === "Escape") setEditingId(null);
                    }}
                    className="flex-1 min-w-0 bg-transparent text-[11px] outline-none border-b border-[var(--border-subtle)]"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRename(item.conversation.id); }}
                    className="text-[var(--color-signal-green)] hover:scale-110 transition-transform"
                  >
                    <Check size={10} weight="bold" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditingId(null); }}
                    className="text-[var(--text-muted)] hover:scale-110 transition-transform"
                  >
                    <X size={10} weight="bold" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium truncate">{item.conversation.title}</p>
                    <p className="text-[9px] text-[var(--text-muted)]">
                      {formatRelativeTime(item.conversation.updatedAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-100">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingId(item.conversation.id);
                        setEditDraft(item.conversation.title);
                      }}
                      className="p-0.5 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]"
                    >
                      <PencilSimpleLine size={10} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteConversation(item.conversation.id);
                      }}
                      className="p-0.5 rounded text-[var(--text-muted)] hover:text-[var(--color-soft-red)] hover:bg-[var(--color-soft-red)]/10"
                    >
                      <Trash size={10} />
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function ChatView() {
  const messages = useAgentChatStore((s) => s.messages);
  const agentStatus = useAgentChatStore((s) => s.agentStatus);
  const streamingContent = useAgentChatStore((s) => s.streamingContent);
  const streamingThinking = useAgentChatStore((s) => s.streamingThinking);
  const sendMessage = useAgentChatStore((s) => s.sendMessage);
  const cancelStream = useAgentChatStore((s) => s.cancelStream);
  const startNewConversation = useAgentChatStore((s) => s.startNewConversation);
  const toggleList = useAgentChatStore((s) => s.toggleConversationList);
  const conversationCount = useAgentChatStore((s) => s.conversationList.length);
  const currentDocument = useDocumentStore((s) => s.currentDocument);
  const currentProject = useDocumentStore((s) => s.currentProject);
  const proposePendingEdit = useDocumentStore((s) => s.proposePendingEdit);
  const cursorLine = useEditorStore((s) => s.cursorLine);
  const selection = useEditorStore((s) => s.selection);

  const scrollRef = useRef<HTMLDivElement>(null);
  const isProcessing = agentStatus !== "idle" && agentStatus !== "done";

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamingContent]);

  const documents = useDocumentStore((s) => s.documents);

  const handleSend = useCallback(
    (message: string, mentionedFileIds?: string[]) => {
      sendMessage({
        message,
        documentId: currentDocument?.id,
        projectId: currentProject?.id,
        referencedFileIds: mentionedFileIds,
        cursorLine: cursorLine,
        selectionStart: selection?.from,
        selectionEnd: selection?.to,
        selectedText: selection?.text,
        onEdit: (edit) => {
          const proposed = edit.full_new_content ?? edit.newContent ?? edit.content;
          if (proposed) {
            proposePendingEdit(proposed, "AI suggested fix");
            return;
          }
          if (edit.search != null && edit.replace != null && currentDocument) {
            const next = currentDocument.content.replace(edit.search, edit.replace);
            if (next !== currentDocument.content) proposePendingEdit(next, "AI suggested fix");
          }
        },
      });
    },
    [sendMessage, currentDocument, currentProject, cursorLine, selection, proposePendingEdit],
  );

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-[var(--border-hairline)]">
        <div className="flex items-center gap-2">
          {conversationCount > 0 && (
            <motion.button
              onClick={toggleList}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="flex items-center gap-1 h-5 rounded-[var(--radius-sm)] px-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)] transition-colors duration-100"
              title="View conversations"
            >
              <ChatCircle size={11} weight="bold" />
              <span className="text-[9px] font-semibold tabular-nums">{conversationCount}</span>
            </motion.button>
          )}
          <span className="text-[10px] font-medium text-[var(--text-muted)]">
            {messages.length > 0 ? `${messages.length} messages` : "New chat"}
          </span>
        </div>
        <motion.button
          onClick={startNewConversation}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="flex items-center justify-center h-5 w-5 rounded-[var(--radius-sm)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)] transition-colors duration-100"
          title="New conversation"
        >
          <Plus size={11} weight="bold" />
        </motion.button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden py-1">
        {messages.length === 0 && !isProcessing && (
          <div className="flex flex-col items-center justify-center h-full px-4 text-center">
            <div className="h-8 w-8 rounded-full bg-[var(--bg-surface-hover)] border border-[var(--border-hairline)] flex items-center justify-center mb-2 text-[var(--text-muted)]">
              <LeverMark size={18} />
            </div>
            <p className="text-[11px] font-medium text-[var(--text-primary)] mb-0.5">
              {APP_NAME} agent
            </p>
            <p className="text-[10px] text-[var(--text-muted)] max-w-[180px]">
              Ask me to edit, fix, explain, or scaffold your document.
            </p>
          </div>
        )}

        <AnimatePresence>
          {messages.map((msg) => (
            <ChatMessageComponent key={msg.id} message={msg} />
          ))}
        </AnimatePresence>

        {isProcessing && (streamingContent || streamingThinking) && (
          <ChatMessageComponent
            message={{
              id: "streaming",
              role: "assistant",
              content: "",
              timestamp: Date.now(),
            }}
            isStreaming
            streamingContent={streamingContent}
            streamingThinking={streamingThinking}
          />
        )}

        {isProcessing && !streamingContent && !streamingThinking && (
          <ThinkingIndicator
            status={agentStatus}
            documentFilename={currentDocument?.filename}
          />
        )}
      </div>

      {/* Input */}
      <ChatInput
        onSend={handleSend}
        isProcessing={isProcessing}
        onCancel={isProcessing ? cancelStream : undefined}
        files={documents.map((d) => ({ id: d.id, filename: d.filename, doc_type: d.doc_type }))}
        activeFileId={currentDocument?.id}
      />
    </div>
  );
}

export function ChatPanel() {
  const showList = useAgentChatStore((s) => s.showConversationList);

  return (
    <div className="h-full">
      <AnimatePresence mode="wait" initial={false}>
        {showList ? (
          <motion.div
            key="list"
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            transition={spring}
            className="h-full"
          >
            <ConversationListView />
          </motion.div>
        ) : (
          <motion.div
            key="chat"
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 8 }}
            transition={spring}
            className="h-full"
          >
            <ChatView />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
